import type { KeyboardEvent, ReactNode } from 'react';
import './picks.css';

export interface PickOption<V extends string = string> {
  value: V;
  label: ReactNode;
  /** Small second line (S0's "Denied boarding, missed connection"). */
  sub?: ReactNode;
}

interface PicksProps<V extends string> {
  options: PickOption<V>[];
  /** null = nothing selected (S9: nothing preselected). */
  value: V | null;
  onChange: (value: V) => void;
  /** Accessible name of the group (usually the screen's question). */
  label: string;
}

/** Arrow keys move the selection, as in a native radio group. */
function arrowNav<V extends string>(
  e: KeyboardEvent<HTMLElement>,
  options: PickOption<V>[],
  value: V | null,
  onChange: (v: V) => void,
) {
  const step = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 }[e.key];
  if (!step || !options.length) return;
  e.preventDefault();
  const i = options.findIndex((o) => o.value === value);
  const next = options[(i + step + options.length) % options.length]!;
  onChange(next.value);
  const btns = e.currentTarget.querySelectorAll<HTMLElement>('[role="radio"]');
  btns[options.indexOf(next)]?.focus();
}

/** Radio-style list: one question, the known answers as rows (design rule 1). */
export function PickList<V extends string>({ options, value, onChange, label }: PicksProps<V>) {
  return (
    <div
      className="picks"
      role="radiogroup"
      aria-label={label}
      onKeyDown={(e) => arrowNav(e, options, value, onChange)}
    >
      {options.map((o, i) => {
        const on = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={on}
            tabIndex={on || (value === null && i === 0) ? 0 : -1}
            className={`pick${on ? ' is-on' : ''}`}
            onClick={() => onChange(o.value)}
          >
            <span className="pick__dot" />
            <span>
              {o.label}
              {o.sub && <span className="pick__sub">{o.sub}</span>}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/** Two large side-by-side choices (S9). */
export function BigPicks<V extends string>({ options, value, onChange, label }: PicksProps<V>) {
  return (
    <div
      className="twoup"
      role="radiogroup"
      aria-label={label}
      onKeyDown={(e) => arrowNav(e, options, value, onChange)}
    >
      {options.map((o, i) => {
        const on = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={on}
            tabIndex={on || (value === null && i === 0) ? 0 : -1}
            className={`bigpick${on ? ' is-on' : ''}`}
            onClick={() => onChange(o.value)}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
